import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Video,
  MapPin,
  Home,
  Navigation,
  Calendar as CalendarIcon,
  Clock,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Stethoscope,
  Search,
  Check,
  Building2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { usePatientLocation } from '@/hooks/usePatientLocation';
import {
  doctorsApi,
  schedulingApi,
  patientsApi,
  type DoctorDetail,
  type Specialty,
  type DependentResponse,
} from '@/api';
import { cn } from '@/utils/cn';
import { InAppDoctorCard } from './InAppDoctorCard';

export interface InAppSchedulingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialModality?: 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR';
  onSuccess?: (appointmentId: string) => void;
}

export type SchedulingModality = 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR';

export function InAppSchedulingModal({
  isOpen,
  onClose,
  initialModality = 'TELEMEDICINA',
  onSuccess,
}: InAppSchedulingModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const {
    coords,
    cep,
    addressLabel,
    isLocating,
    error: locationError,
    hasLocation,
    requestGpsLocation,
    setCep,
  } = usePatientLocation();

  // Etapa atual: 1 = Modalidade/Local | 2 = Médico | 3 = Horário/Paciente | 4 = Confirmação/Sucesso
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [modality, setModality] = useState<SchedulingModality>(initialModality);
  const [cepInput, setCepInput] = useState(cep);

  // Filtros de médicos
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string | null>(null);
  const [searchDoctor, setSearchDoctor] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDetail | null>(null);

  // Agendamento
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [selectedSlotTime, setSelectedSlotTime] = useState<string>('');
  const [selectedDependentId, setSelectedDependentId] = useState<string | null>(null);
  const [chiefComplaint, setChiefComplaint] = useState('');

  // Sincroniza modalidade inicial
  useEffect(() => {
    if (isOpen) {
      setModality(initialModality);
      setStep(1);
      setSelectedDoctor(null);
      setSelectedSlotId('');
    }
  }, [isOpen, initialModality]);

  useEffect(() => {
    setCepInput(cep);
  }, [cep]);

  // Carrega especialidades médicas
  const { data: specialties = [] } = useQuery<Specialty[]>({
    queryKey: ['scheduling', 'specialties'],
    queryFn: () => doctorsApi.getSpecialties(),
    enabled: isOpen,
    staleTime: 1000 * 60 * 10,
  });

  // Carrega médicos disponíveis de acordo com a modalidade e geolocalização
  const isGeoNeeded = modality === 'PRESENCIAL' || modality === 'DOMICILIAR';
  const { data: doctorsData, isLoading: isLoadingDoctors } = useQuery({
    queryKey: [
      'scheduling',
      'doctors',
      modality,
      selectedSpecialtyId,
      searchDoctor,
      isGeoNeeded ? coords?.lat : null,
      isGeoNeeded ? coords?.lng : null,
      isGeoNeeded ? cep : null,
    ],
    queryFn: async () => {
      const res = await doctorsApi.getDoctors({
        modality,
        specialty: selectedSpecialtyId || undefined,
        search: searchDoctor.trim() || undefined,
        latitude: isGeoNeeded ? coords?.lat : undefined,
        longitude: isGeoNeeded ? coords?.lng : undefined,
        cep: isGeoNeeded && cep ? cep : undefined,
      });
      return (res as any)?.data || (res as any)?.doctors || [];
    },
    enabled: isOpen && step >= 2,
  });

  // Carrega dependentes do paciente
  const { data: dependents = [] } = useQuery<DependentResponse[]>({
    queryKey: ['patient', 'dependents', user?.id],
    queryFn: async () => {
      const res = await patientsApi.getDependents(1, 50);
      return res?.dependents || [];
    },
    enabled: isOpen && !!user,
  });

  // Carrega slots do médico selecionado
  const { data: availableSlotsData, isLoading: isLoadingSlots } = useQuery({
    queryKey: ['scheduling', 'doctor-slots', selectedDoctor?.id, selectedDate],
    queryFn: async () => {
      if (!selectedDoctor?.id || !selectedDate) return [];
      const res = await schedulingApi.getAvailableSlots(selectedDoctor.id, selectedDate);
      return res?.slots || (Array.isArray(res) ? res : []);
    },
    enabled: isOpen && !!selectedDoctor?.id && !!selectedDate,
  });

  // Helpers de Fuso Horário de São Paulo (Brasil)
  const getSaoPauloDateStr = (d: Date = new Date()): string => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const getSaoPauloTimeStr = (d: Date = new Date()): string => {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  };

  // Próximas datas disponíveis no fuso de São Paulo (hoje + 7 dias)
  const nextDates = useMemo(() => {
    const list: Array<{ label: string; dateStr: string; dayNumber: string; dayWeek: string }> = [];
    const todayStr = getSaoPauloDateStr();
    const parts = todayStr.split('-').map(Number);
    const y = parts[0] || new Date().getFullYear();
    const m = (parts[1] || 1) - 1;
    const d = parts[2] || 1;
    const baseDate = new Date(y, m, d, 12, 0, 0);

    for (let i = 0; i < 7; i++) {
      const cur = new Date(baseDate);
      cur.setDate(baseDate.getDate() + i);
      const curDateStr = getSaoPauloDateStr(cur);
      const dayNumber = curDateStr.split('-')[2] || '01';
      const dayWeek = cur.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
      list.push({
        label: i === 0 ? 'Hoje' : i === 1 ? 'Amanhã' : `${dayWeek}, ${dayNumber}`,
        dateStr: curDateStr,
        dayNumber,
        dayWeek: dayWeek.toUpperCase(),
      });
    }
    return list;
  }, []);

  // Filtra horários disponíveis respeitando fuso de São Paulo e bloqueando horários passados
  const filteredSlots = useMemo(() => {
    const todayStr = getSaoPauloDateStr();
    const currentTimeStr = getSaoPauloTimeStr();
    const isToday = selectedDate === todayStr;

    const sourceSlots = availableSlotsData && availableSlotsData.length > 0
      ? availableSlotsData
      : [
          { id: 'slot-0800', startTime: '08:00', available: true },
          { id: 'slot-0900', startTime: '09:00', available: true },
          { id: 'slot-1000', startTime: '10:00', available: true },
          { id: 'slot-1100', startTime: '11:00', available: true },
          { id: 'slot-1400', startTime: '14:00', available: true },
          { id: 'slot-1500', startTime: '15:00', available: true },
          { id: 'slot-1600', startTime: '16:00', available: true },
          { id: 'slot-1700', startTime: '17:00', available: true },
        ];

    return sourceSlots.map((slot: any) => {
      const sTime = slot.startTime || '10:00';
      const isPast = isToday && sTime <= currentTimeStr;
      const isAvailable = (slot.available ?? (slot.status === 'AVAILABLE' || slot.status === undefined)) && !isPast;
      return {
        ...slot,
        id: slot.id || `slot-${sTime.replace(':', '')}`,
        startTime: sTime,
        isPast,
        isAvailable,
      };
    });
  }, [availableSlotsData, selectedDate]);

  // Seleciona automaticamente a primeira data disponível
  useEffect(() => {
    if (!selectedDate && nextDates.length > 0 && nextDates[0]?.dateStr) {
      setSelectedDate(nextDates[0].dateStr);
    }
  }, [nextDates, selectedDate]);

  // Mutação para criar agendamento
  const createAppointmentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDoctor?.id) throw new Error('Selecione um médico');
      const scheduledAtIso =
        selectedDate && selectedSlotTime
          ? `${selectedDate}T${selectedSlotTime}:00.000Z`
          : undefined;

      return schedulingApi.createAppointment({
        doctorId: selectedDoctor.id,
        slotId: selectedSlotId || 'default-slot',
        scheduledAt: scheduledAtIso,
        serviceType: modality,
        reason: chiefComplaint.trim() || undefined,
        dependentId: selectedDependentId || undefined,
      });
    },
    onSuccess: (data) => {
      setStep(4);
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['patient', 'appointments'] });
      if (onSuccess) onSuccess(data.id);
    },
  });

  const handleCepSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cepInput.trim()) {
      setCep(cepInput);
    }
  };

  const handleNextFromStep1 = () => {
    setStep(2);
  };

  const handleSelectDoctor = (doctor: any) => {
    setSelectedDoctor(doctor);
    setStep(3);
  };

  const handleSelectSlot = (doctor: any, dateStr: string, slotTime: string, slotId: string) => {
    setSelectedDoctor(doctor);
    setSelectedDate(dateStr);
    setSelectedSlotTime(slotTime === 'Agora' ? getSaoPauloTimeStr() : slotTime);
    setSelectedSlotId(slotId);
    setStep(3);
  };

  const handleConfirmBooking = () => {
    createAppointmentMutation.mutate();
  };

  // Formatação de preço considerando a modalidade selecionada
  const getDoctorPriceForModality = (doctor?: any) => {
    if (!doctor) return 120;
    if (modality === 'PRESENCIAL' && doctor.inPersonPrice) return Number(doctor.inPersonPrice);
    if (modality === 'DOMICILIAR' && doctor.homeCarePrice) return Number(doctor.homeCarePrice);
    if (modality === 'TELEMEDICINA' && doctor.telemedicinePrice) return Number(doctor.telemedicinePrice);
    if (doctor.startingPrice) return Number(doctor.startingPrice);
    if (doctor.consultationPrice) {
      return doctor.consultationPrice >= 1000 ? doctor.consultationPrice / 100 : doctor.consultationPrice;
    }
    return 120;
  };

  const formatPrice = (price?: number | null) => {
    if (!price) return 'R$ 120,00';
    const val = price >= 1000 ? price / 100 : price;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl xl:max-w-6xl w-[96vw] sm:w-full p-0 overflow-hidden rounded-2xl sm:rounded-3xl bg-card border border-border shadow-2xl flex flex-col h-[92vh] sm:max-h-[90vh]">
        {/* Header Elegante do Modal */}
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-border/80 bg-muted/30 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-base sm:text-lg font-bold text-foreground leading-tight truncate">
                Novo Agendamento
              </DialogTitle>
              <DialogDescription className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 truncate">
                {step === 1 && 'Escolha a modalidade e informe sua localização'}
                {step === 2 && 'Selecione o médico especialista de sua preferência'}
                {step === 3 && 'Escolha o melhor dia, horário e dados da consulta'}
                {step === 4 && 'Agendamento confirmado com sucesso'}
              </DialogDescription>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
              <span>Etapa {step} de 4</span>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Corpo do Modal com Scroll Suave */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-5 sm:space-y-6">
          {/* ============================================================== */}
          {/* ETAPA 1: Modalidade & Localização GPS                           */}
          {/* ============================================================== */}
          {step === 1 && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Seletor Visual de Modalidades */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  1. Como você deseja ser atendido?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Telemedicina */}
                  <div
                    onClick={() => setModality('TELEMEDICINA')}
                    className={cn(
                      'p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between gap-3 text-left',
                      modality === 'TELEMEDICINA'
                        ? 'border-cyan-600 bg-cyan-50/50 dark:bg-cyan-950/20 shadow-xs'
                        : 'border-border/80 hover:border-cyan-600/40 hover:bg-muted/30'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center text-cyan-600">
                        <Video className="w-4 h-4" />
                      </div>
                      {modality === 'TELEMEDICINA' && (
                        <CheckCircle2 className="w-4 h-4 text-cyan-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-foreground">Telemedicina Medikei</h4>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                        Atendimento online por vídeo sem sair de casa
                      </p>
                    </div>
                  </div>

                  {/* Consultório Presencial */}
                  <div
                    onClick={() => setModality('PRESENCIAL')}
                    className={cn(
                      'p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between gap-3 text-left',
                      modality === 'PRESENCIAL'
                        ? 'border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs'
                        : 'border-border/80 hover:border-blue-600/40 hover:bg-muted/30'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600">
                        <Building2 className="w-4 h-4" />
                      </div>
                      {modality === 'PRESENCIAL' && (
                        <CheckCircle2 className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-foreground">Em Consultório</h4>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                        Consulta física na clínica ou consultório do médico
                      </p>
                    </div>
                  </div>

                  {/* Médico em Casa */}
                  <div
                    onClick={() => setModality('DOMICILIAR')}
                    className={cn(
                      'p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col justify-between gap-3 text-left',
                      modality === 'DOMICILIAR'
                        ? 'border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20 shadow-xs'
                        : 'border-border/80 hover:border-emerald-600/40 hover:bg-muted/30'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-emerald-600">
                        <Home className="w-4 h-4" />
                      </div>
                      {modality === 'DOMICILIAR' && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-foreground">Médico em Casa</h4>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                        Visita médica no conforto do seu domicílio
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção Obrigatória de Geolocalização (Quando Presencial ou Domiciliar) */}
              {isGeoNeeded && (
                <div className="p-4 rounded-xl bg-muted/40 border border-border space-y-3 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-1.5">
                      <Navigation className="w-4 h-4 text-primary" />
                      {modality === 'DOMICILIAR'
                        ? 'Local para Atendimento em Domicílio'
                        : 'Sua Localização para Consultório Próximo'}
                    </span>
                    {hasLocation && (
                      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">
                        ✓ Localização Ativa
                      </Badge>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Para exibir apenas médicos com raio de cobertura na sua região e calcular a distância real,
                    ative o GPS do dispositivo ou informe seu CEP.
                  </p>

                  <div className="flex flex-col sm:flex-row items-center gap-2 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => requestGpsLocation()}
                      disabled={isLocating}
                      className={cn(
                        'w-full sm:w-auto text-xs font-semibold gap-2 shrink-0 h-9',
                        coords ? 'border-emerald-600 text-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/20' : ''
                      )}
                    >
                      {isLocating ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Obtendo GPS...</span>
                        </>
                      ) : (
                        <>
                          <MapPin className="w-3.5 h-3.5" />
                          <span>{coords ? 'Atualizar meu GPS' : 'Usar meu local (GPS)'}</span>
                        </>
                      )}
                    </Button>

                    <form onSubmit={handleCepSubmit} className="flex items-center gap-1.5 w-full">
                      <input
                        type="text"
                        placeholder="Ou digite seu CEP (ex: 01310-100)"
                        value={cepInput}
                        onChange={(e) => setCepInput(e.target.value)}
                        onBlur={() => cepInput && setCep(cepInput)}
                        maxLength={9}
                        className="h-9 px-3 rounded-lg border border-border bg-background text-xs flex-1 focus:outline-none focus:ring-1 focus:ring-primary font-medium"
                      />
                      <Button type="submit" size="sm" variant="secondary" className="text-xs h-9 px-3 shrink-0">
                        Aplicar CEP
                      </Button>
                    </form>
                  </div>

                  {addressLabel && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 p-2 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      <Check className="w-3.5 h-3.5 shrink-0" />
                      <span className="font-medium truncate">{addressLabel}</span>
                    </div>
                  )}

                  {locationError && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 rounded-lg border border-amber-200 dark:border-amber-800">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{locationError}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Botão de Avançar Etapa 1 */}
              <div className="pt-2 flex justify-end">
                <Button
                  onClick={handleNextFromStep1}
                  disabled={isGeoNeeded && !hasLocation}
                  className="gap-2 px-6"
                >
                  <span>Continuar para Escolha do Médico</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* ETAPA 2: Especialidade & Escolha do Médico                     */}
          {/* ============================================================== */}
          {step === 2 && (
            <div className="space-y-4 animate-in fade-in duration-200">
              {/* Barra de Filtros Completa com Busca, Modalidade e Especialidades */}
              <div className="space-y-3.5">
                {/* Linha Superior: Busca e Modalidades Inline */}
                <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Buscar por médico, especialidade ou CRM..."
                      value={searchDoctor}
                      onChange={(e) => setSearchDoctor(e.target.value)}
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-input bg-card text-foreground text-xs sm:text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-xs"
                    />
                  </div>

                  {/* Seletor de Modalidades Inline */}
                  <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1 md:pb-0">
                    <button
                      type="button"
                      onClick={() => setModality('TELEMEDICINA')}
                      className={cn(
                        'px-3 py-2 text-xs rounded-xl font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer border',
                        modality === 'TELEMEDICINA'
                          ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                          : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border-border/60'
                      )}
                    >
                      <Video className="w-3.5 h-3.5" />
                      <span>Telemedicina</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setModality('PRESENCIAL')}
                      className={cn(
                        'px-3 py-2 text-xs rounded-xl font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer border',
                        modality === 'PRESENCIAL'
                          ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                          : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border-border/60'
                      )}
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      <span>Consultório</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setModality('DOMICILIAR')}
                      className={cn(
                        'px-3 py-2 text-xs rounded-xl font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer border',
                        modality === 'DOMICILIAR'
                          ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                          : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground border-border/60'
                      )}
                    >
                      <Home className="w-3.5 h-3.5" />
                      <span>Domiciliar</span>
                    </button>
                  </div>
                </div>

                {/* Pílulas de Especialidades com Destaque */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar text-xs">
                  <button
                    type="button"
                    onClick={() => setSelectedSpecialtyId(null)}
                    className={cn(
                      'px-3.5 py-1.5 rounded-full font-bold transition-all shrink-0 cursor-pointer border',
                      !selectedSpecialtyId
                        ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                        : 'bg-card text-muted-foreground hover:text-foreground border-border hover:border-primary/40'
                    )}
                  >
                    Todas Especialidades
                  </button>
                  {specialties.map((spec) => (
                    <button
                      key={spec.id}
                      type="button"
                      onClick={() => setSelectedSpecialtyId(spec.id)}
                      className={cn(
                        'px-3.5 py-1.5 rounded-full font-bold transition-all shrink-0 cursor-pointer border',
                        selectedSpecialtyId === spec.id
                          ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                          : 'bg-card text-muted-foreground hover:text-foreground border-border hover:border-primary/40'
                      )}
                    >
                      {spec.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lista de Médicos Estilo Doctoralia / Landing Page com Mini Agenda */}
              {isLoadingDoctors ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <span className="text-sm font-semibold">Buscando especialistas credenciados e horários disponíveis...</span>
                </div>
              ) : doctorsData && doctorsData.length === 0 ? (
                <div className="py-12 text-center space-y-3.5 border border-dashed rounded-3xl p-8 bg-muted/20">
                  <Stethoscope className="w-10 h-10 text-muted-foreground mx-auto" />
                  <div>
                    <h4 className="font-bold text-base text-foreground">Nenhum médico encontrado</h4>
                    <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto leading-relaxed">
                      {isGeoNeeded
                        ? 'Não encontramos médicos disponíveis nesta região no momento. Tente ampliar seu CEP ou optar por Telemedicina.'
                        : 'Tente alterar os termos da busca ou selecionar outra especialidade médica.'}
                    </p>
                  </div>
                  {isGeoNeeded && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setModality('TELEMEDICINA');
                        setStep(1);
                      }}
                      className="text-xs rounded-xl"
                    >
                      Mudar para Telemedicina Medikei
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {doctorsData?.map((doctor: any) => (
                    <InAppDoctorCard
                      key={doctor.id}
                      doctor={doctor}
                      currentModality={modality}
                      formattedPrice={formatPrice(getDoctorPriceForModality(doctor))}
                      onSelectDoctor={handleSelectDoctor}
                      onSelectSlot={handleSelectSlot}
                    />
                  ))}
                </div>
              )}

              {/* Botão de Voltar */}
              <div className="pt-2 flex justify-between">
                <Button variant="ghost" onClick={() => setStep(1)} className="gap-2 text-xs font-semibold">
                  <ArrowLeft className="w-4 h-4" />
                  <span>Voltar para Modalidade</span>
                </Button>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* ETAPA 3: Data, Horário & Paciente                              */}
          {/* ============================================================== */}
          {step === 3 && selectedDoctor && (
            <div className="space-y-5 animate-in fade-in duration-200">
              {/* Card Resumo do Médico Escolhido */}
              <div className="p-3 rounded-xl bg-muted/40 border border-border flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">
                    {(selectedDoctor.profile?.displayName || 'Dr').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-foreground">
                      {selectedDoctor.profile?.displayName || 'Médico'}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {selectedDoctor.primarySpecialty?.name || 'Clínico Geral'} • CRM {selectedDoctor.crm}/{selectedDoctor.crmState}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-muted-foreground block">Valor</span>
                  <span className="text-sm font-bold text-foreground">
                    {formatPrice(getDoctorPriceForModality(selectedDoctor))}
                  </span>
                </div>
              </div>

              {/* Seletor de Datas Rápidas */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Escolha o Dia
                </label>
                <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                  {nextDates.map((item) => (
                    <button
                      key={item.dateStr}
                      type="button"
                      onClick={() => setSelectedDate(item.dateStr)}
                      className={cn(
                        'px-3.5 py-2 rounded-xl text-center border transition-all shrink-0',
                        selectedDate === item.dateStr
                          ? 'border-primary bg-primary text-white shadow-xs'
                          : 'border-border bg-card hover:bg-muted text-foreground'
                      )}
                    >
                      <span className="block text-[10px] font-semibold opacity-80">{item.dayWeek}</span>
                      <span className="block text-base font-bold">{item.dayNumber}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Seletor de Horários */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Horários Disponíveis
                </label>
                {isLoadingSlots ? (
                  <div className="py-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span>Carregando horários da agenda...</span>
                  </div>
                ) : filteredSlots.length === 0 || filteredSlots.every((s) => !s.isAvailable) ? (
                  <div className="p-4 rounded-xl border border-dashed text-center space-y-2 bg-muted/20">
                    <Clock className="w-5 h-5 text-muted-foreground mx-auto" />
                    <p className="text-xs text-muted-foreground">
                      {selectedDate === getSaoPauloDateStr()
                        ? 'Todos os horários de hoje já passaram pelo fuso de Brasília/São Paulo.'
                        : 'Nenhum horário disponível para esta data.'}
                    </p>
                    {nextDates[1] && (
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        onClick={() => setSelectedDate(nextDates[1]?.dateStr || '')}
                        className="text-xs h-8 font-semibold"
                      >
                        Ver horários de amanhã ({nextDates[1]?.dayNumber} - {nextDates[1]?.dayWeek})
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-56 overflow-y-auto pr-1">
                    {filteredSlots.map((slot) => {
                      const isSelected = selectedSlotId === slot.id || selectedSlotTime === slot.startTime;
                      return (
                        <button
                          key={slot.id || slot.startTime}
                          type="button"
                          disabled={!slot.isAvailable}
                          onClick={() => {
                            setSelectedSlotId(slot.id);
                            setSelectedSlotTime(slot.startTime);
                          }}
                          title={
                            slot.isPast
                              ? 'Horário já ultrapassado hoje'
                              : !slot.isAvailable
                              ? 'Horário indisponível ou já reservado'
                              : `Selecionar ${slot.startTime}`
                          }
                          className={cn(
                            'py-2.5 px-2 rounded-xl border text-xs font-semibold transition-all text-center',
                            !slot.isAvailable
                              ? 'opacity-40 cursor-not-allowed bg-muted/60 text-muted-foreground border-border/50 line-through'
                              : isSelected
                              ? 'border-primary bg-primary text-white shadow-xs font-bold'
                              : 'border-border hover:border-primary/50 text-foreground bg-card'
                          )}
                        >
                          {slot.startTime}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Paciente a ser atendido (Titular ou Dependente) */}
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Quem será atendido?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div
                    onClick={() => setSelectedDependentId(null)}
                    className={cn(
                      'p-2.5 rounded-lg border cursor-pointer text-xs flex items-center justify-between',
                      selectedDependentId === null
                        ? 'border-primary bg-primary/5 font-semibold text-primary'
                        : 'border-border text-foreground hover:bg-muted/40'
                    )}
                  >
                    <span>Para mim ({user?.name || user?.email})</span>
                    {selectedDependentId === null && <Check className="w-3.5 h-3.5" />}
                  </div>

                  {dependents.map((dep) => (
                    <div
                      key={dep.id}
                      onClick={() => setSelectedDependentId(dep.id)}
                      className={cn(
                        'p-2.5 rounded-lg border cursor-pointer text-xs flex items-center justify-between',
                        selectedDependentId === dep.id
                          ? 'border-primary bg-primary/5 font-semibold text-primary'
                          : 'border-border text-foreground hover:bg-muted/40'
                      )}
                    >
                      <span className="truncate">{dep.name} ({dep.relationship || 'Dependente'})</span>
                      {selectedDependentId === dep.id && <Check className="w-3.5 h-3.5" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Motivo da consulta (opcional) */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Motivo da Consulta (Opcional)
                </label>
                <input
                  type="text"
                  placeholder="Ex: Check-up de rotina, dor de cabeça, sintomas gripais..."
                  value={chiefComplaint}
                  onChange={(e) => setChiefComplaint(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Botões de Ação Etapa 3 */}
              <div className="pt-2 flex justify-between items-center">
                <Button variant="ghost" onClick={() => setStep(2)} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span>Voltar aos Médicos</span>
                </Button>

                <Button
                  onClick={handleConfirmBooking}
                  disabled={createAppointmentMutation.isPending || !selectedSlotTime}
                  className="gap-2 px-6 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {createAppointmentMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Confirmando...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Confirmar Agendamento</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* ETAPA 4: Confirmação e Sucesso Imediato                         */}
          {/* ============================================================== */}
          {step === 4 && (
            <div className="py-8 text-center space-y-5 animate-in zoom-in-95 duration-200">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 flex items-center justify-center mx-auto shadow-sm">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div className="space-y-1">
                <h3 className="text-xl font-bold text-foreground">Consulta Agendada com Sucesso!</h3>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                  Seu agendamento foi registrado e já está visível na sua lista de próximas consultas no painel.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-muted/30 border border-border max-w-md mx-auto text-left text-xs space-y-2">
                <div className="flex justify-between border-b border-border/60 pb-2">
                  <span className="text-muted-foreground">Médico:</span>
                  <span className="font-bold text-foreground">{selectedDoctor?.profile?.displayName}</span>
                </div>
                <div className="flex justify-between border-b border-border/60 pb-2">
                  <span className="text-muted-foreground">Modalidade:</span>
                  <span className="font-bold text-foreground">
                    {modality === 'TELEMEDICINA' && 'Telemedicina Medikei'}
                    {modality === 'PRESENCIAL' && 'Consultório Presencial'}
                    {modality === 'DOMICILIAR' && 'Médico em Casa (Domiciliar)'}
                  </span>
                </div>
                <div className="flex justify-between border-b border-border/60 pb-2">
                  <span className="text-muted-foreground">Data e Horário:</span>
                  <span className="font-bold text-foreground">
                    {selectedDate.split('-').reverse().join('/')} às {selectedSlotTime}
                  </span>
                </div>
                {addressLabel && isGeoNeeded && (
                  <div className="flex justify-between pt-1">
                    <span className="text-muted-foreground">Local/Região:</span>
                    <span className="font-semibold text-emerald-600 truncate max-w-[200px]">{addressLabel}</span>
                  </div>
                )}
              </div>

              <div className="pt-3 flex justify-center gap-3">
                <Button onClick={onClose} className="px-6">
                  Concluir e Ver Minha Agenda
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
